// src/components/Sidebar.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Layout, Menu, Typography, Drawer } from "antd";
import {
  CarOutlined,
  SettingOutlined,
  ContactsOutlined,
  TeamOutlined,
  DashboardOutlined,
  NodeIndexOutlined,
  FileTextOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  LinkOutlined,
  CarryOutOutlined,
  MedicineBoxOutlined,
  FileDoneOutlined,
  BankOutlined,
  ControlOutlined,
  AuditOutlined,
  ApartmentOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../UserContext";
import logo from "../Assets/img/logo.webp";
import "./Sidebar.css"; // Importa el CSS personalizado
import "../styles/modern-layout.css"; // Importa los estilos modernos

const { Sider } = Layout;
const { Title } = Typography;

function Sidebar({ open, toggleDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useUser();

  // Detectar si el dispositivo es móvil
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Define móvil como <= 768px
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleListItemClick = (route) => {
    console.log("Sidebar click, route=", route);
    navigate(`/modules/${route}`);
    if (isMobile) {
      toggleDrawer();
    }
  };

  // Función para verificar si un módulo está disponible
  const isModuleAvailable = (moduleKey) => {
    if (!user) return false;

    // Solo si el usuario es administrador Y está sin rol, tiene acceso a todo
    if (user.es_administrador && user.sinRol) return true;

    // En cualquier otro caso, verifica el estado del módulo (case insensitive)
    const modulo = user.modulos?.find(
      (m) => m.key?.toLowerCase() === moduleKey.toLowerCase()
    );
    return modulo?.estado === "disponible";
  };

  const menuItems = [
    {
      label: "Dashboard",
      key: "Dashboard",
      icon: <DashboardOutlined />,
      onClick: () => handleListItemClick("Dashboard"),
    },
    isModuleAvailable("Usuarios") && {
      label: "Usuarios",
      key: "user",
      icon: <TeamOutlined />,
      children: [
        {
          label: "Lista de Usuarios",
          key: "UserList",
          icon: <UnorderedListOutlined />,
          onClick: () => handleListItemClick("UserList"),
        },
        {
          label: "Tarjetas de Usuarios",
          key: "UserCards",
          icon: <IdcardOutlined />,
          onClick: () => handleListItemClick("UserCards"),
        },
        {
          label: "Roles y Permisos",
          key: "roles",
          icon: <SafetyCertificateOutlined />,
          onClick: () => handleListItemClick("roles"),
        },
      ],
    },
    isModuleAvailable("Transporte") && {
      label: "Transporte",
      key: "Transport",
      icon: <CarOutlined />,
      children: [
        {
          label: "Conductores",
          key: "Drivers",
          icon: <SolutionOutlined />,
          onClick: () => handleListItemClick("Drivers"),
        },
        {
          label: "Vehículos",
          key: "Vehicles",
          icon: <CarOutlined />,
          onClick: () => handleListItemClick("Vehicles"),
        },
        {
          label: "Asignaciones",
          key: "VehiculoConductores",
          icon: <LinkOutlined />,
          onClick: () => handleListItemClick("VehiculoConductores"),
        },
        {
          label: "Pre Operacionales",
          key: "preoperations",
          icon: <CarryOutOutlined />,
          onClick: () => handleListItemClick("preoperations"),
        },
      ],
    },
    isModuleAvailable("Pacientes") && {
      label: "Pacientes",
      key: "Patients",
      icon: <MedicineBoxOutlined />,
      onClick: () => handleListItemClick("Patients"),
    },
    isModuleAvailable("Autorizaciones") && {
      label: "Autorizaciones",
      key: "Authorizations",
      icon: <FileDoneOutlined />,
      onClick: () => handleListItemClick("Authorizations"),
    },
    isModuleAvailable("Clientes") && {
      label: "Clientes EPS",
      key: "Clients",
      icon: <ContactsOutlined />,
      onClick: () => handleListItemClick("Clients"),
    },
    isModuleAvailable("Instituciones") && {
      label: "Instituciones",
      key: "Institutions",
      icon: <BankOutlined />,
      onClick: () => handleListItemClick("Institutions"),
    },
    isModuleAvailable("Centro de Control") && {
      label: "Centro de Control",
      key: "CenterControl",
      icon: <ControlOutlined />,
      onClick: () => handleListItemClick("CenterControl"),
    },
    isModuleAvailable("Coordinacion") && {
      label: "Coordinación",
      key: "Coordinacion",
      icon: <NodeIndexOutlined />,
      onClick: () => handleListItemClick("Coordinacion"),
    },
    isModuleAvailable("Control Servicios") && {
      label: "Control Servicios",
      key: "ControlServicios",
      icon: <AuditOutlined />,
      onClick: () => handleListItemClick("ControlServicios"),
    },
    isModuleAvailable("Transportadoras") && {
      label: "Transportadoras",
      key: "Transportadoras",
      icon: <ApartmentOutlined />,
      onClick: () => handleListItemClick("Transportadoras"),
    },
    isModuleAvailable("TAI") && {
      label: "TAI",
      key: "TAIMenu",
      icon: <CarOutlined style={{ color: "#722ed1" }} />,
      children: [
        {
          label: "Coordinación TAI",
          key: "TAI",
          icon: <NodeIndexOutlined />,
          onClick: () => handleListItemClick("TAI"),
        },
        {
          label: "Planillas TAI",
          key: "PlanillasTAI",
          icon: <FileTextOutlined />,
          onClick: () => handleListItemClick("PlanillasTAI"),
        },
        {
          label: "Estadísticas TAI",
          key: "TAIEstadisticas",
          icon: <BarChartOutlined />,
          onClick: () => handleListItemClick("TAIEstadisticas"),
        },
      ],
    },
    isModuleAvailable("FUEC") && {
      label: "FUEC",
      key: "FUECMenu",
      icon: <FileTextOutlined style={{ color: "#1a5276" }} />,
      children: [
        {
          label: "Lista FUEC",
          key: "Fuec",
          icon: <FileTextOutlined />,
          onClick: () => handleListItemClick("Fuec"),
        },
        {
          label: "Objetos Contrato",
          key: "ObjetosContrato",
          icon: <ProfileOutlined />,
          onClick: () => handleListItemClick("ObjetosContrato"),
        },
        {
          label: "Configuración FUEC",
          key: "FuecConfig",
          icon: <SettingOutlined />,
          onClick: () => handleListItemClick("FuecConfig"),
        },
      ],
    },
    // Configuraciones - Solo para administradores
    user?.es_administrador && {
      label: "Configuraciones",
      key: "Configuraciones",
      icon: <SettingOutlined />,
      onClick: () => handleListItemClick("Configuraciones"),
    },
  ].filter(Boolean);

  const sidebarContent = (
    <>
      <div className="sidebar-logo">
        <img src={logo} alt="Logo" style={{ width: open ? "80%" : "100%" }} />
        {open && (
          <Title level={5} className="sidebar-title">
            Módulos
          </Title>
        )}
      </div>
      {open && <div className="tech-sidebar-divider"></div>}
      <div
        className="sidebar-custom"
        style={{
          overflowY: "auto",
          height: "calc(100vh - 160px)",
        }}
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname.split("/")[2]]}
          defaultOpenKeys={["user"]}
          items={menuItems}
        />
      </div>
    </>
  );

  return isMobile ? (
    <Drawer
      title={<span style={{ color: "white" }}>Menú</span>}
      placement="left"
      onClose={toggleDrawer}
      open={open}
      className="custom-drawer custom-drawer-content-wrapper modern-sidebar" // Añade las clases personalizadas
      closeIcon={<span style={{ color: "white" }}>X</span>}
    >
      {sidebarContent}
    </Drawer>
  ) : (
    <Sider
      width={250}
      collapsible
      trigger={null}
      collapsed={!open}
      theme="dark"
      breakpoint="lg"
      collapsedWidth="80"
      className="modern-sidebar"
      style={{
        color: "white",
        overflow: "hidden",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000,
      }}
    >
      {sidebarContent}
    </Sider>
  );
}

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
};

export default Sidebar;
